import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import RouteCanonical from "@/components/seo/RouteCanonical";
import { useSearchParams } from "react-router-dom";
import { useJobs, type JobsSort } from "@/hooks/useJobs";
import { useAlgoliaJobs, useDebouncedValue } from "@/hooks/useAlgoliaJobs";
import { useJobBoardStats } from "@/hooks/useJobBoardStats";
import BoardHero from "./sections/BoardHero";
import BoardRail from "./sections/BoardRail";
import BoardJobRow from "./sections/BoardJobRow";
import CascadeList from "./sections/CascadeList";
import { BoardPayCard, BoardCopilotCard, BoardBrowse } from "./sections/BoardExtras";
import DataAnswers from "@/components/DataAnswers";
import HomeFooter from "./sections/HomeFooter";
import JobPagination from "@/components/JobPagination";
import { OfferSlot } from "@/components/OfferSlot";
import { rotateOffer } from "@/lib/offers";
import CopilotNudge, { type NudgeHandle } from "@/components/copilot/CopilotNudge";
import QuizModal from "@/components/copilot/QuizModal";
import { siteEvents } from "@/lib/siteEvents";
import { useHomeLive } from "@/hooks/useHomeLive";
import "./v2-styles.css";
import "./board.css";

const PAGE_SIZE = 20;

// URL param helpers — strip empty/false/page=1 from the URL.
const buildParams = (input: Record<string, string | boolean | number | undefined | null>) => {
  const next = new URLSearchParams();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    if (k === "page" && (v === 1 || v === "1")) continue;
    next.set(k, String(v));
  }
  return next;
};

const HomeV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate from URL (matches AllJobs param names).
  // `q` is the homepage hero's handoff param; `title` is the board's own.
  const urlTitle = searchParams.get("title") || searchParams.get("q") || "";
  const urlLocation = searchParams.get("location") || "";
  const urlRemote = searchParams.get("remote") === "true";
  const urlInternship = searchParams.get("internship") === "true";
  const urlContract = searchParams.get("contract") === "true";
  const urlPartTime = searchParams.get("parttime") === "true";
  const urlCategory = searchParams.get("category") || "";
  const urlPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const ALLOWED_SORTS: JobsSort[] = ['relevance', 'newest', 'salary_high', 'salary_low'];
  const rawSort = searchParams.get("sort") as JobsSort | null;
  const urlSort: JobsSort = rawSort && ALLOWED_SORTS.includes(rawSort) ? rawSort : 'relevance';

  // Local input state (the hero search input doesn't filter live — must submit).
  const [jobTitle, setJobTitle] = useState(urlTitle);
  const [location, setLocation] = useState(urlLocation);
  const [isRemote, setIsRemote] = useState(urlRemote);
  const [isInternship, setIsInternship] = useState(urlInternship);
  const [isContract, setIsContract] = useState(urlContract);
  const [isPartTime, setIsPartTime] = useState(urlPartTime);

  // Re-sync local inputs when URL changes (e.g. back/forward nav).
  useEffect(() => {
    setJobTitle(urlTitle);
    setLocation(urlLocation);
    setIsRemote(urlRemote);
    setIsInternship(urlInternship);
    setIsContract(urlContract);
    setIsPartTime(urlPartTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // What actually drives the query: URL state (single source of truth).
  // Category replaces title; only one of (title, category) is active.
  const effectiveTitle = urlCategory || urlTitle;

  // --- Instant search (Algolia) --------------------------------------------
  // Typing (or an active category) drives instant results from the af_search
  // index (last 60 days, relevance-ranked, synonym + typo aware). Toggles and
  // location apply live on that path. Non-relevance sorts and default browse
  // stay on the Supabase path (full inventory, server-side sort).
  const debouncedTitle = useDebouncedValue(jobTitle, 250);
  const debouncedLocation = useDebouncedValue(location, 250);
  const instantQuery = debouncedTitle.trim() || urlCategory;
  const algoliaActive = instantQuery !== "" && urlSort === "relevance";

  const [algoliaPage, setAlgoliaPage] = useState(1);
  useEffect(() => {
    setAlgoliaPage(1);
  }, [instantQuery, debouncedLocation, isRemote, isInternship, isContract, isPartTime]);

  const hoursSelected = [
    ...(isInternship ? ["Internship", "Intern"] : []),
    ...(isContract ? ["Contract"] : []),
    ...(isPartTime ? ["Part-Time"] : []),
  ];

  const supabaseJobs = useJobs({
    page: urlPage,
    pageSize: PAGE_SIZE,
    jobTitle: algoliaActive ? "" : effectiveTitle,
    location: algoliaActive ? "" : urlLocation,
    isRemote: algoliaActive ? false : urlRemote,
    isInternship: algoliaActive ? false : urlInternship,
    isContract: algoliaActive ? false : urlContract,
    isPartTime: algoliaActive ? false : urlPartTime,
    sort: algoliaActive ? "relevance" : urlSort,
  });

  const algolia = useAlgoliaJobs({
    query: instantQuery,
    location: debouncedLocation,
    isRemote,
    hours: hoursSelected,
    page: algoliaPage,
    pageSize: PAGE_SIZE,
    enabled: algoliaActive,
  });

  const jobs = algoliaActive ? algolia.data : supabaseJobs.data;
  const isLoading = algoliaActive ? algolia.isLoading : supabaseJobs.isLoading;
  const totalCount = algoliaActive ? algolia.totalCount : supabaseJobs.totalCount;

  // The result set is restricted whenever instant search is live or any URL
  // search/filter/category is in effect; sort alone still shows the whole board.
  const searchActive =
    algoliaActive ||
    Boolean(effectiveTitle || urlLocation || urlRemote || urlInternship || urlContract || urlPartTime);

  const { data: stats } = useJobBoardStats();

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );

  // --- URL writers ---------------------------------------------------------

  const updateUrl = (patch: Record<string, string | boolean | number | undefined | null>) => {
    // Merge current URL state with patch, then strip empties.
    // Sort is preserved across search/category/page changes (ecommerce-style).
    const merged: Record<string, string | boolean | number | null> = {
      title: searchParams.get("title") || searchParams.get("q") || "",
      location: searchParams.get("location") || "",
      remote: searchParams.get("remote") === "true",
      internship: searchParams.get("internship") === "true",
      contract: searchParams.get("contract") === "true",
      parttime: searchParams.get("parttime") === "true",
      category: searchParams.get("category") || "",
      page: parseInt(searchParams.get("page") || "1", 10) || 1,
      sort: searchParams.get("sort") || "",
      ...patch,
    };
    setSearchParams(buildParams(merged), { replace: false });
  };

  const handleSortChange = (value: JobsSort) => {
    // Strip 'relevance' (default) from URL; reset to page 1 since results re-order.
    updateUrl({ sort: value === 'relevance' ? '' : value, page: 1 });
  };

  const submitSearch = () => {
    // Hero search clears active category (one-of rule).
    updateUrl({
      title: jobTitle.trim(),
      location: location.trim(),
      remote: isRemote,
      internship: isInternship,
      contract: isContract,
      parttime: isPartTime,
      category: "",
      page: 1,
    });
    // Show the user their results: glide down to the list. (Delay lets the
    // URL-change re-render settle so the smooth scroll isn't canceled by it.)
    setTimeout(() => {
      const el = document.querySelector(".results");
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 12;
      window.scrollTo({ top, behavior: "smooth" });
    }, 300);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitSearch();
  };

  const handleCategory = (categoryKey: string) => {
    if (!categoryKey) {
      // "All roles" — clear category only.
      updateUrl({ category: "", page: 1 });
      return;
    }
    // Category replaces hero search input.
    setJobTitle("");
    updateUrl({ category: categoryKey, title: "", page: 1 });
  };

  const handlePageChange = (page: number) => {
    if (algoliaActive) {
      setAlgoliaPage(page);
    } else {
      updateUrl({ page });
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // --- Co-pilot nudge -------------------------------------------------------
  const nudge = useRef<NudgeHandle>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  // A thin instant search is the strongest "stuck" signal we have.
  useEffect(() => {
    if (algoliaActive && !algolia.isLoading && instantQuery) nudge.current?.results(algolia.totalCount, instantQuery);
  }, [algoliaActive, algolia.isLoading, algolia.totalCount, instantQuery]);

  const { data: live } = useHomeLive();
  const families = live?.pay?.families;

  const toggles = { remote: urlRemote, internship: urlInternship, contract: urlContract, parttime: urlPartTime };
  const onToggle = (key: keyof typeof toggles) => {
    nudge.current?.signal("filter");
    const next = !toggles[key];
    if (key === "remote") setIsRemote(next);
    if (key === "internship") setIsInternship(next);
    if (key === "contract") setIsContract(next);
    if (key === "parttime") setIsPartTime(next);
    updateUrl({ [key]: next, page: 1 });
  };

  // Featured (paid) rows stay first, as before. The pay card follows the
  // fourth ordinary row, the cross-promotion slot the sixth, the Co-pilot
  // card the eighth.
  const featuredCount = jobs.filter((j) => j.isFeatured || j.is_featured).length;
  const slots: Record<number, "pay" | "offer" | "copilot"> = {
    [featuredCount + 4]: "pay",
    [featuredCount + 6]: "offer",
    [featuredCount + 8]: "copilot",
  };
  // Job-seeker products only. Still Remote is listed twice so it shows on half
  // the days: it is the only offer drawing clicks (7 days to 2026-10-27).
  const gridOffer = rotateOffer(["still_remote", "search_sprint", "still_remote", "hired"]);
  const shownTotal = searchActive ? totalCount : stats?.totalCount ?? totalCount;
  const headline = searchActive
    ? `${totalCount.toLocaleString("en-US")} matching ${totalCount === 1 ? "role" : "roles"}`
    : "Latest roles";

  return (
    <div className="v2-root">
      <RouteCanonical />
      <Helmet>
        <title>Accounting & Finance Jobs | Audit Friendly</title>
        <meta name="description" content="Accounting and finance jobs read from employers' own career pages and checked hourly, with posted pay where the employer states it." />
      </Helmet>


      <BoardHero
        jobTitle={jobTitle}
        setJobTitle={setJobTitle}
        location={location}
        setLocation={setLocation}
        onSubmit={(e) => { nudge.current?.signal("search"); handleSubmit(e); }}
        total={live?.stats?.active_jobs ?? stats?.totalCount}
        new24h={live?.strip?.new_24h}
      />

      <div className="container bh-layout">
        <BoardRail
          types={stats?.types}
          category={urlCategory}
          onCategory={(label) => { nudge.current?.signal("filter"); handleCategory(label); }}
          toggles={toggles}
          onToggle={onToggle}
          remoteCount={stats?.remoteCount}
        />

        <section className="results bh-results" aria-labelledby="bh-results-h">
          {algoliaActive && (
            <DataAnswers query={[instantQuery, debouncedLocation].filter(Boolean).join(" ")} />
          )}
          <div className="bh-results__head">
            <div>
              <h2 id="bh-results-h" className="bh-results__h">{headline}</h2>
              {algoliaActive && !algolia.isLoading ? (
                <p className="bh-results__sub bh-results__instant">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                  {algolia.totalCount.toLocaleString("en-US")} matches in {algolia.processingTimeMS} ms, as you type
                </p>
              ) : !searchActive && stats?.recentJobsCount ? (
                <p className="bh-results__sub">{stats.recentJobsCount.toLocaleString("en-US")} new in the last 7 days</p>
              ) : null}
            </div>
            <label className="bh-sort">
              <span>Sort</span>
              <select value={urlSort} onChange={(e) => handleSortChange(e.target.value as JobsSort)}>
                <option value="relevance">Most relevant</option>
                <option value="newest">Newest first</option>
                <option value="salary_high">Pay: high to low</option>
                <option value="salary_low">Pay: low to high</option>
              </select>
            </label>
          </div>

          {algoliaActive && !isLoading && totalCount === 0 && (
            <div className="bh-empty">
              <p className="bh-empty__h">No matches for &ldquo;{instantQuery}&rdquo;</p>
              <p className="bh-empty__s">Try a broader title, or one of these:</p>
              <div className="bh-empty__chips">
                {["Senior Accountant", "Controller", "FP&A Analyst", "Tax Manager", "AP Specialist", "Payroll"].map((s) => (
                  <button key={s} type="button" onClick={() => setJobTitle(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {isLoading && jobs.length === 0 && (
            <div aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="bj-row bj-row--ghost" />)}</div>
          )}

          <div className="bh-list">
            <CascadeList
              items={jobs.flatMap((job, i) => [
                ...(slots[i] === "pay" ? [{ key: "slot-pay", node: <BoardPayCard query={effectiveTitle || instantQuery} families={families} /> }] : []),
                ...(slots[i] === "offer" ? [{ key: "slot-offer", node: <OfferSlot offerKey={gridOffer} surface="home_grid" className="af-offer--grid" /> }] : []),
                ...(slots[i] === "copilot" ? [{ key: "slot-copilot", node: <BoardCopilotCard onOpen={() => { siteEvents.act("copilot_inline_open", { source: "board" }); setQuizOpen(true); }} /> }] : []),
                { key: `job-${job.id}`, node: <BoardJobRow job={job} families={families} onOpen={() => nudge.current?.signal("job_open")} /> },
              ])}
            />
          </div>

          {totalPages > 1 && (
            <div className="v2-pagination">
              <JobPagination currentPage={algoliaActive ? algoliaPage : urlPage} totalPages={totalPages} goToPage={handlePageChange} />
            </div>
          )}
          {jobs.length > 0 && (
            <p className="bh-results__progress">Showing {jobs.length} of {shownTotal.toLocaleString("en-US")} roles</p>
          )}
        </section>
      </div>

      <BoardBrowse
        effectiveTitle={effectiveTitle}
        urlLocation={urlLocation}
        urlRemote={urlRemote}
        urlInternship={urlInternship}
        urlContract={urlContract}
        urlPartTime={urlPartTime}
      />

      <HomeFooter />
      <CopilotNudge ref={nudge} source="board" paused={quizOpen} context={{ title: effectiveTitle || instantQuery, location: urlLocation || debouncedLocation }} />
      {quizOpen && <QuizModal open onClose={() => setQuizOpen(false)} source="board-inline" />}
    </div>
  );
};

export default HomeV2;
